import { Box, alpha, useTheme } from '@mui/material';
import { hueColor, type CardHue } from './hues';
import type { ReactNode } from 'react';

/**
 * The tinted tile a card's icon sits in.
 *
 * `DetailCard` has always had an `icon` slot and rendered it as a bare grey
 * glyph, which reads as decoration. A tinted tile gives a card a visual anchor
 * and lets a reader tell the sections apart while scanning rather than by
 * reading every heading.
 *
 * **The tone is a CATEGORY, never a severity.** `risk` here means "this card is
 * about the thing that went wrong", not "this is critical" — severity is the
 * risk badge's job and it carries a word, because colour never carries meaning
 * alone in this product. Four tones is deliberately few: a palette with one
 * colour per card is a rainbow nobody can read.
 */
export type CardIconTone = 'risk' | 'info' | 'good' | 'neutral';

export function CardIcon({
  children,
  tone = 'info',
  hue,
  size = 'medium',
}: {
  children: ReactNode;
  tone?: CardIconTone;
  /** Overrides `tone`. Used by the concept vocabulary to vary by section. */
  hue?: CardHue;
  /**
   * `small` for a tile inside a control rather than at the head of a card.
   *
   * A second SIZE rather than a second component: the thing that must not fork
   * is the hue and what it means, and a 32px tile beside a caption-sized label
   * on a question card would dominate the question it is labelling.
   */
  size?: 'small' | 'medium';
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const color = hue
    ? hueColor(hue, dark)
    : tone === 'risk'
      ? theme.palette.risk.HIGH
      : tone === 'good'
        ? theme.palette.success.main
        : tone === 'neutral'
          ? theme.palette.text.secondary
          : theme.palette.primary.main;

  return (
    <Box
      aria-hidden
      sx={{
        display: 'grid',
        placeItems: 'center',
        width: size === 'small' ? 24 : 32,
        height: size === 'small' ? 24 : 32,
        flexShrink: 0,
        borderRadius: 1.25,
        color,
        bgcolor: alpha(color, 0.12),
        '& .MuiSvgIcon-root': { fontSize: size === 'small' ? 15 : 18 },
      }}
    >
      {children}
    </Box>
  );
}
